import { useState, useEffect } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Loader2, Mail, CheckCircle2, ArrowRight, ShieldCheck,
  RefreshCw, Sparkles, Zap, Rocket, Check, Gift
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import api from "@/lib/api";

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const nav = useNavigate();
  const { verifyEmail, resendVerification, user } = useAuth();

  const queryEmail = searchParams.get("email") || "";
  const queryToken = searchParams.get("token") || "";

  const [email, setEmail] = useState(queryEmail);
  const [otp, setOtp] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [verified, setVerified] = useState(false);
  const [verifiedData, setVerifiedData] = useState(null);
  const [plans, setPlans] = useState([]);
  const [cycle, setCycle] = useState("monthly");

  // Load public plans for price plans display
  useEffect(() => {
    api.get("/billing/plans/public")
      .then(({ data }) => setPlans(data?.plans || []))
      .catch(() => {});
  }, []);

  // If token is in URL, auto verify
  useEffect(() => {
    if (queryToken && queryEmail && !verified) {
      handleAutoVerify(queryEmail, queryToken);
    }
  }, [queryToken, queryEmail]);

  // Cooldown countdown timer
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setInterval(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  const handleAutoVerify = async (targetEmail, token) => {
    setVerifying(true);
    try {
      const data = await verifyEmail(targetEmail, { token });
      setVerified(true);
      setVerifiedData(data);
      toast.success("Email confirmed! 7-Day Free Trial activated.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Link expired or invalid. Please enter OTP manually.");
    } finally {
      setVerifying(false);
    }
  };

  const handleOtpSubmit = async (e) => {
    e?.preventDefault();
    const cleanOtp = otp.trim();
    if (!email.trim()) {
      toast.error("Please enter your email address");
      return;
    }
    if (!cleanOtp || cleanOtp.length < 6) {
      toast.error("Please enter the 6-digit confirmation code");
      return;
    }
    setVerifying(true);
    try {
      const data = await verifyEmail(email.trim(), { otp: cleanOtp });
      setVerified(true);
      setVerifiedData(data);
      toast.success("Email verified successfully! 7-Day Free Trial is now active.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid or expired verification code");
    } finally {
      setVerifying(false);
    }
  };

  const handleResend = async () => {
    if (!email.trim()) {
      toast.error("Please enter your email to resend code");
      return;
    }
    if (resendCooldown > 0) return;
    setResending(true);
    try {
      await resendVerification(email.trim());
      setResendCooldown(45);
      toast.success(`Fresh verification code sent to ${email.trim()}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to resend verification code");
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#F8F9FA] dark:bg-slate-950 text-slate-900 dark:text-slate-100">
      {/* Header */}
      <header className="border-b border-slate-200 dark:border-slate-800 bg-white/80 dark:bg-slate-900/80 backdrop-blur sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-black border border-yellow-500/40 flex items-center justify-center font-black text-amber-400 text-sm shadow-xs">
              iHUB
            </div>
            <span className="font-extrabold text-xl tracking-tight font-sans">LaunchDeck</span>
          </Link>
          <div className="flex items-center gap-3">
            <Badge className="bg-blue-600/10 text-blue-600 dark:text-blue-400 border border-blue-500/20 px-3 py-1 font-semibold text-xs">
              7-Day Free Trial
            </Badge>
          </div>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center p-6 sm:p-10">
        {!verified ? (
          /* ── Verification Step (OTP or Link) ── */
          <Card className="w-full max-w-lg p-8 border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-xl rounded-2xl" data-testid="verify-email-card">
            <div className="text-center mb-8">
              <div className="w-16 h-16 rounded-2xl bg-blue-50 dark:bg-blue-950/60 border border-blue-200 dark:border-blue-800 flex items-center justify-center mx-auto mb-4 text-blue-600">
                <Mail className="w-8 h-8" />
              </div>
              <h1 className="text-2xl font-bold tracking-tight">Confirm Your Email</h1>
              <p className="text-slate-500 dark:text-slate-400 text-xs sm:text-sm mt-2 max-w-sm mx-auto leading-relaxed">
                We've sent a 6-digit confirmation code and a 1-click confirmation link to:
              </p>
              <div className="mt-2 inline-block font-semibold text-sm text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/40 px-3 py-1 rounded-full border border-blue-200 dark:border-blue-900">
                {email || "your email address"}
              </div>
            </div>

            <form onSubmit={handleOtpSubmit} className="space-y-6">
              {!queryEmail && (
                <div>
                  <label className="text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider block mb-1">
                    Your Email Address
                  </label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="name@company.com"
                    className="h-10 text-sm"
                    required
                  />
                </div>
              )}

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                    6-Digit Verification Code
                  </label>
                  <span className="text-[11px] text-slate-500">Valid for 24 hours</span>
                </div>
                <Input
                  data-testid="otp-input"
                  type="text"
                  maxLength={6}
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                  placeholder="e.g. 123456"
                  className="h-14 text-center font-mono text-2xl tracking-[0.4em] font-bold border-2 border-blue-500/30 focus-visible:border-blue-600 focus-visible:ring-blue-500/20"
                  autoFocus
                />
              </div>

              <div className="rounded-xl p-3.5 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-800 text-xs text-slate-600 dark:text-slate-300 space-y-1">
                <div className="font-semibold flex items-center gap-1.5 text-blue-600 dark:text-blue-400">
                  <Gift className="w-4 h-4 shrink-0" />
                  <span>Dual Confirmation Options</span>
                </div>
                <p className="leading-relaxed">
                  You can type the 6-digit code above, or simply click the <strong>Activate 7-Day Free Trial</strong> link in your email inbox!
                </p>
              </div>

              <Button
                data-testid="verify-otp-btn"
                type="submit"
                disabled={verifying || otp.length < 6}
                className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm shadow-md"
              >
                {verifying ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Verifying Email...
                  </>
                ) : (
                  <>
                    Verify &amp; Activate 7-Day Free Trial <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>

              <div className="flex items-center justify-between pt-2 text-xs">
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={resending || resendCooldown > 0}
                  className="text-blue-600 hover:underline flex items-center gap-1 font-medium disabled:opacity-50"
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${resending ? "animate-spin" : ""}`} />
                  {resendCooldown > 0 ? `Resend code in ${resendCooldown}s` : "Resend verification code"}
                </button>

                <Link to="/signin" className="text-slate-500 hover:text-slate-800 dark:hover:text-slate-200">
                  Back to Sign In
                </Link>
              </div>
            </form>
          </Card>
        ) : (
          /* ── Step 2 / Price Plans & Trial Activated Display ── */
          <div className="w-full max-w-5xl py-8 space-y-8 animate-in fade-in zoom-in-95 duration-300" data-testid="pricing-trial-view">
            {/* Celebration Banner */}
            <div className="rounded-2xl p-6 sm:p-8 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 text-white shadow-xl text-center relative overflow-hidden">
              <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 blur-3xl rounded-full pointer-events-none" />
              <div className="relative z-10 space-y-3">
                <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider bg-white/20 text-white backdrop-blur border border-white/25">
                  <Sparkles className="w-4 h-4" /> 7-Day Free Trial Activated
                </div>
                <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">
                  Welcome to LaunchDeck!
                </h1>
                <p className="text-sm sm:text-base text-blue-100 max-w-2xl mx-auto leading-relaxed">
                  Your email has been verified. You now have unrestricted access to all <strong>Pro Features</strong> for the next 7 days. No credit card required.
                </p>
                <div className="pt-2">
                  <Button
                    onClick={() => nav("/")}
                    data-testid="go-to-dashboard-btn"
                    size="lg"
                    className="bg-white hover:bg-slate-100 text-blue-700 font-bold px-8 shadow-lg"
                  >
                    Go to My Studio Dashboard <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            </div>

            {/* Price Plans Showcase */}
            <div className="space-y-6">
              <div className="text-center space-y-2">
                <h2 className="text-2xl font-bold tracking-tight">Our Price Plans</h2>
                <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">
                  Transparent plans built for creators, studios, and multi-location marketing agencies.
                </p>

                {/* Billing cycle toggle */}
                <div className="inline-flex rounded-full p-1 border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 mt-2">
                  <button
                    onClick={() => setCycle("monthly")}
                    className={`px-4 py-1 text-xs font-semibold rounded-full transition-all ${
                      cycle === "monthly" ? "bg-blue-600 text-white" : "text-slate-600 dark:text-slate-400"
                    }`}
                  >
                    Monthly
                  </button>
                  <button
                    onClick={() => setCycle("yearly")}
                    className={`px-4 py-1 text-xs font-semibold rounded-full transition-all ${
                      cycle === "yearly" ? "bg-blue-600 text-white" : "text-slate-600 dark:text-slate-400"
                    }`}
                  >
                    Yearly <span className="opacity-80">· save 20%</span>
                  </button>
                </div>
              </div>

              {/* Grid of Plans */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* 1. Free / Starter Plan */}
                <Card className="p-6 border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 rounded-xl relative flex flex-col justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-9 h-9 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-600 dark:text-slate-400">
                        <Sparkles className="w-5 h-5" />
                      </div>
                      <div className="text-xl font-bold">Free</div>
                    </div>
                    <div className="mb-4">
                      <span className="text-4xl font-extrabold">$0</span>
                      <span className="text-xs text-slate-500 ml-1">forever</span>
                    </div>
                    <p className="text-xs text-slate-500 mb-4">Basic essentials for individuals testing the waters.</p>
                    <ul className="space-y-2.5 text-xs text-slate-600 dark:text-slate-300">
                      <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-500" /> 500 contacts</li>
                      <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-500" /> 1,000 emails / month</li>
                      <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-500" /> 20 AI generations / month</li>
                      <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-500" /> 1 studio location</li>
                      <li className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-500" /> Standard community support</li>
                    </ul>
                  </div>
                  <Button variant="outline" className="w-full mt-6 text-xs" onClick={() => nav("/")}>
                    Basic Free Tier
                  </Button>
                </Card>

                {/* 2. Pro Plan (Active 7-Day Free Trial Tier) */}
                <Card className="p-6 border-2 border-blue-600 dark:border-blue-500 bg-white dark:bg-slate-900 rounded-xl shadow-xl relative -translate-y-2 flex flex-col justify-between">
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[11px] font-bold text-white bg-blue-600 shadow-sm flex items-center gap-1">
                    <Sparkles className="w-3 h-3" /> YOUR ACTIVE 7-DAY TRIAL
                  </div>

                  <div>
                    <div className="flex items-center gap-2 mb-3 mt-2">
                      <div className="w-9 h-9 rounded-lg bg-blue-50 dark:bg-blue-950 flex items-center justify-center text-blue-600">
                        <Zap className="w-5 h-5" />
                      </div>
                      <div className="text-xl font-bold">Pro Tier</div>
                    </div>
                    <div className="mb-2">
                      <span className="text-4xl font-extrabold">${cycle === "yearly" ? 23 : 29}</span>
                      <span className="text-xs text-slate-500 ml-1">/mo after trial</span>
                    </div>
                    <Badge className="bg-emerald-500 text-white text-[11px] mb-4">
                      Full Access Active · 7 Days Remaining
                    </Badge>
                    <p className="text-xs text-slate-500 mb-4">Unrestricted access to all professional marketing tools.</p>
                    <ul className="space-y-2.5 text-xs text-slate-600 dark:text-slate-300">
                      <li className="flex items-center gap-2 font-medium text-blue-600 dark:text-blue-400">
                        <Check className="w-4 h-4 text-blue-600" /> 5,000 contacts & CRM
                      </li>
                      <li className="flex items-center gap-2 font-medium text-blue-600 dark:text-blue-400">
                        <Check className="w-4 h-4 text-blue-600" /> 25,000 email sends / month
                      </li>
                      <li className="flex items-center gap-2 font-medium text-blue-600 dark:text-blue-400">
                        <Check className="w-4 h-4 text-blue-600" /> 500 AI generations & Copilot
                      </li>
                      <li className="flex items-center gap-2 font-medium text-blue-600 dark:text-blue-400">
                        <Check className="w-4 h-4 text-blue-600" /> 5 team members & sub-accounts
                      </li>
                      <li className="flex items-center gap-2 font-medium text-blue-600 dark:text-blue-400">
                        <Check className="w-4 h-4 text-blue-600" /> Automated pipelines & campaigns
                      </li>
                    </ul>
                  </div>

                  <Button
                    onClick={() => nav("/")}
                    className="w-full mt-6 bg-blue-600 hover:bg-blue-700 text-white font-semibold text-xs h-10"
                  >
                    Enter Dashboard on Free Trial &rarr;
                  </Button>
                </Card>

                {/* 3. Scale / Agency Plan */}
                <Card className="p-6 border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 rounded-xl relative flex flex-col justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-9 h-9 rounded-lg bg-orange-50 dark:bg-orange-950 flex items-center justify-center text-orange-600">
                        <Rocket className="w-5 h-5" />
                      </div>
                      <div className="text-xl font-bold">Scale / Agency</div>
                    </div>
                    <div className="mb-4">
                      <span className="text-4xl font-extrabold">${cycle === "yearly" ? 79 : 99}</span>
                      <span className="text-xs text-slate-500 ml-1">/mo</span>
                    </div>
                    <p className="text-xs text-slate-500 mb-4">For agencies managing high client volume & multiple studios.</p>
                    <ul className="space-y-2.5 text-xs text-slate-600 dark:text-slate-300">
                      <li className="flex items-center gap-2"><Check className="w-4 h-4 text-orange-500" /> Unlimited contacts</li>
                      <li className="flex items-center gap-2"><Check className="w-4 h-4 text-orange-500" /> 100,000 emails / month</li>
                      <li className="flex items-center gap-2"><Check className="w-4 h-4 text-orange-500" /> 5,000 AI generations</li>
                      <li className="flex items-center gap-2"><Check className="w-4 h-4 text-orange-500" /> Unlimited locations & sub-accounts</li>
                      <li className="flex items-center gap-2"><Check className="w-4 h-4 text-orange-500" /> Custom branding & white-labeling</li>
                    </ul>
                  </div>

                  <Button
                    variant="outline"
                    className="w-full mt-6 text-xs border-orange-500/40 text-orange-600 hover:bg-orange-500/10"
                    onClick={() => nav("/billing")}
                  >
                    Upgrade to Agency Scale
                  </Button>
                </Card>
              </div>

              {/* Bottom direct link */}
              <div className="text-center pt-4">
                <Button
                  onClick={() => nav("/")}
                  variant="ghost"
                  className="text-xs text-slate-500 hover:text-slate-800 dark:hover:text-slate-200"
                >
                  Skip pricing and continue directly to Dashboard &rarr;
                </Button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

